import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import type { GameState } from '@/lib/golfEngine';
import { getFrontHoles, getBackHoles, playerReceivesStrokeOnHole, getBetBreakdown } from '@/lib/golfEngine';
import { generateRoundIntelligence, type RIInput, RoundIntelligenceSection } from '@/lib/roundIntelligence';
import { computeBestStrokeUtilization, BestStrokeUtilizationSection } from '@/lib/strokeUtilization';
import { getGameEngine, isPressCapable } from '@/games/index';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, RotateCcw, Trophy, DollarSign, Check, Loader2, BookOpen } from 'lucide-react';
import { AttachToRoundModal } from '@/components/AttachToRoundModal';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { usePlayerStats } from '@/hooks/use-round-lifecycle';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { SettlementStepper, type SettlementLine } from '@/components/SettlementStepper';

const TIE_FINALIZE_STORAGE_KEY = 'twoDownAutoPlus_gameState';
const TIE_FINALIZE_ROUND_ID_KEY = 'twoDownAutoPlus_roundId';

/**
 * @param skip When true (review-local-scores mode), skip all server and local
 *   settlement calculations. The returned lines will be null and isLoading false.
 *   This ensures the ReviewLocalScores path never displays amounts as confirmed.
 */
function useSettlementData(gameState: GameState, skip?: boolean) {
  const [lines, setLines] = useState<SettlementLine[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Gate B: tracks whether a server settlement fetch failed for a server-backed
  // round. When true the component shows an explicit error + retry instead of
  // silently substituting a local calculation — which would be visually
  // indistinguishable from server-confirmed data and could display wrong amounts.
  const [settlementFetchFailed, setSettlementFetchFailed] = useState(false);
  // Incrementing this triggers a retry without unmounting the component.
  const [fetchKey, setFetchKey] = useState(0);
  const engine = getGameEngine(gameState.gameType);

  useEffect(() => {
    // Review-local-scores mode: do NOT fetch from server or compute local
    // settlement. The caller explicitly wants score display only.
    if (skip) return;

    if (!gameState.roundId) {
      // True guest round (no server record) — local engine output is the only
      // source of truth and is always correct here. Fallback is intentional.
      const local = engine.settleGame(gameState);
      if (local.totalAmountForA === 0) {
        setLines([]);
      } else {
        const team1Name = gameState.matchFormat === '1v1'
          ? (gameState.teams[0]?.players[0]?.name ?? 'Team 1')
          : `${gameState.teams[0]?.players[0]?.name ?? ''} & ${gameState.teams[0]?.players[1]?.name || ''}`;
        const team2Name = gameState.matchFormat === '1v1'
          ? (gameState.teams[1]?.players[0]?.name ?? 'Team 2')
          : `${gameState.teams[1]?.players[0]?.name ?? ''} & ${gameState.teams[1]?.players[1]?.name || ''}`;
        setLines([{
          fromName: local.totalAmountForA < 0 ? team1Name : team2Name,
          toName: local.totalAmountForA > 0 ? team1Name : team2Name,
          amount: Math.abs(local.totalAmountForA),
        }]);
      }
      return;
    }

    // Server-backed round: fetch authoritative settlements.
    setIsLoading(true);
    setSettlementFetchFailed(false);
    fetch(`/api/rounds/${gameState.roundId}/settlement`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        // Server now returns { isFinalized, roundStatus, settlements: [...] }.
        // A bare-array response lacks isFinalized and roundStatus, so it cannot
        // prove server finalization. Treating it as authoritative would recreate
        // the silent-false-confirmation bug this Gate B fix is designed to close.
        // If a bare array arrives (e.g. a stale server during rolling deploy),
        // surface the explicit error UI so the user knows to retry — never display
        // unconfirmed amounts as confirmed settlement data.
        if (Array.isArray(data)) {
          console.error('[ResultsScreen] Received legacy bare-array settlement response — missing isFinalized proof. Surfacing error UI instead of displaying unconfirmed amounts.');
          setSettlementFetchFailed(true);
          return;
        }
        const settlementArr: any[] = data.settlements ?? [];
        const mapped: SettlementLine[] = settlementArr.map((s: any) => ({
          fromName: s.fromName,
          toName: s.toName,
          amount: s.amount,
          venmoLink: s.venmoLink,
          venmoWebLink: s.venmoWebLink,
          paypalLink: s.paypalLink,
          settlementId: s.settlementId,
          settlementStatus: s.settlementStatus,
          winnerPaymentMethods: s.winnerPaymentMethods,
        }));
        setLines(mapped);
        setSettlementFetchFailed(false);
      })
      .catch((err) => {
        // Gate B fix: do NOT substitute a local calculation for server-backed
        // rounds. The local engine output may differ from the server-confirmed
        // settlement (handicap differences, server-side rounding, press state).
        // Showing a local estimate as authoritative violates INVARIANT 4.
        // Surface an explicit error instead so the user knows what they are seeing.
        console.error('[ResultsScreen] Settlement fetch failed for server-backed round:', err);
        setSettlementFetchFailed(true);
        // Leave lines as null — the error UI handles display.
      })
      .finally(() => setIsLoading(false));
  }, [gameState.roundId, fetchKey, skip]);

  const retrySettlementFetch = () => setFetchKey(k => k + 1);

  return { lines, isLoading, setLines, settlementFetchFailed, retrySettlementFetch };
}

interface ResultsScreenProps {
  gameState: GameState;
  onEditScores?: () => void;
  onNewRound?: () => void;
  readOnly?: boolean;
  editScoresError?: string | null;
  isAttached?: boolean;
  isOrganizer?: boolean;
  groupId?: string | null;
  /**
   * Gate B — Review Local Scores mode.
   *
   * When true the component is in a read-only review of local device scores
   * entered during the active round. No server data is fetched, no settlement
   * is displayed, and a prominent banner explicitly labels the content as
   * "Local scores — not server-confirmed". `onReturnToRecovery` must be
   * provided so the user can navigate back to the recovery_required screen.
   */
  reviewLocalScores?: boolean;
  onReturnToRecovery?: () => void;
}

function MatchResultCard({ settlementLines }: { settlementLines: Array<{ amount: number; toName: string; fromName: string }> }) {
  const perPlayerAmount = settlementLines[0].amount;
  const allMatch = settlementLines.every(l => l.amount === perPlayerAmount);
  const winnerNames = settlementLines.map(l => l.toName).filter((v, i, a) => a.indexOf(v) === i);
  const loserNames = settlementLines.map(l => l.fromName).filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="settlement-line-0">
      {!allMatch && (
        <p className="text-xs text-amber-600 font-semibold mb-2 text-center" data-testid="settlement-mismatch-warning">Settlement amounts differ — contact organizer</p>
      )}
      <div className="text-center space-y-1">
        <p className="font-semibold text-sm text-green-700" data-testid="settlement-winner">{winnerNames.join(' & ')}</p>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">beat</p>
        <p className="font-semibold text-sm text-red-600" data-testid="settlement-loser">{loserNames.join(' & ')}</p>
        <p className="font-bold text-2xl text-gray-800 pt-1" data-testid="settlement-amount-0">
          ${perPlayerAmount.toFixed(2)}
        </p>
      </div>
    </div>
  );
}

export function ResultsScreen({ gameState, onEditScores, onNewRound, readOnly, editScoresError, isAttached, isOrganizer, groupId, reviewLocalScores, onReturnToRecovery }: ResultsScreenProps) {
  const [stepperOpen, setStepperOpen] = useState(false);
  const [venmoInput, setVenmoInput] = useState('');
  const [paypalInput, setPaypalInput] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [tieFinalizing, setTieFinalizing] = useState(false);
  const [tieFinalizeError, setTieFinalizeError] = useState<string | null>(null);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const engine = getGameEngine(gameState.gameType);
  const pressEng = isPressCapable(engine) ? engine : null;
  const nassau = engine.getMatchStatus?.(gameState) ?? { front: 0, back: 0, overall: 0 };
  const pressStatuses = pressEng?.getPressStatuses(gameState) ?? {};
  const { user, isAuthenticated } = useAuth();
  // Gate B: pass skip=true in reviewLocalScores mode to prevent any server
  // settlement fetch or local calculation from running. We must never display
  // money amounts as confirmed when we know the server round is still active.
  const { lines: settlementLines, isLoading: settlementLoading, setLines: setSettlementLines, settlementFetchFailed, retrySettlementFetch } = useSettlementData(gameState, reviewLocalScores);
  const localSettlement = engine.settleGame(gameState);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const handleSavePaymentHandles = async () => {
    if (!venmoInput.trim() && !paypalInput.trim()) return;
    setProfileSaving(true);
    try {
      const body: Record<string, string> = {};
      if (venmoInput.trim()) body.venmoUsername = venmoInput.trim();
      if (paypalInput.trim()) body.paypalUsername = paypalInput.trim();
      await apiRequest('PATCH', '/api/auth/user/profile', body);
      setProfileSaved(true);
    } catch {
    } finally {
      setProfileSaving(false);
    }
  };

  const handleFinalizeTiedRound = async () => {
    if (tieFinalizing) return;
    if (!gameState.roundId) return;
    setTieFinalizing(true);
    setTieFinalizeError(null);
    try {
      await apiRequest('POST', `/api/rounds/${gameState.roundId}/finalize`, {});
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/pending'] });
      queryClient.invalidateQueries({ queryKey: ['/api/rounds', gameState.roundId, 'lifecycle'] });
      try {
        localStorage.removeItem(TIE_FINALIZE_STORAGE_KEY);
        localStorage.removeItem(TIE_FINALIZE_ROUND_ID_KEY);
      } catch {}
      toast({
        title: 'Match is even — pushed to ledger. No money changes hands.',
        duration: 4000,
      });
      await new Promise(resolve => setTimeout(resolve, 700));
      navigate('/ledger');
    } catch (err: any) {
      setTieFinalizeError(err?.message || 'Failed to finalize round. Please try again.');
    } finally {
      setTieFinalizing(false);
    }
  };

  const userMissingPaymentHandles = isAuthenticated && user && !(user as any).venmoUsername && !(user as any).paypalUsername;

  const allPlayerNames = gameState.teams.flatMap(t => t.players.map(p => p.name.toLowerCase()));
  const userDisplayName = user?.firstName
    ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`.toLowerCase()
    : '';
  const isParticipant = isAuthenticated && userDisplayName
    ? allPlayerNames.some(n => n === userDisplayName || n.includes(userDisplayName) || userDisplayName.includes(n))
    : false;
  const showRazzyStrip = isAuthenticated && isParticipant && !!user?.personId;
  const { stats: playerStats } = usePlayerStats(showRazzyStrip ? user?.personId : null);
  const frontHoles = getFrontHoles(gameState.startingHole);
  const backHoles = getBackHoles(gameState.startingHole);

  // ─── Round Intelligence input ────────────────────────────────────────────
  let nassauAmountForA: number | null = null;
  let pressAmountForA: number | null = null;
  try {
    const breakdown = getBetBreakdown(gameState);
    let nForA = 0, pForA = 0;
    for (const legData of breakdown) {
      const stake = legData.leg === 'Front' ? gameState.stakes.front
        : legData.leg === 'Back' ? gameState.stakes.back
        : gameState.stakes.overall;
      for (const bet of legData.bets) {
        const sign = bet.winner === 'A' ? 1 : bet.winner === 'B' ? -1 : 0;
        const amt = sign * bet.unitCount * stake;
        if (bet.pressIndex === 0) nForA += amt;
        else pForA += amt;
      }
    }
    nassauAmountForA = nForA;
    pressAmountForA = pForA;
  } catch { /* skip */ }
  const riInput: RIInput = {
    teams: gameState.teams as [{ players: { id: string; name: string; strokesReceived: number }[] }, { players: { id: string; name: string; strokesReceived: number }[] }],
    scores: gameState.scores.map(s => ({
      playerId: s.playerId,
      holeNumber: s.holeNumber,
      grossScore: s.grossScore ?? null,
      netScore: s.netScore ?? null,
      receivesStroke: s.receivesStroke ?? false,
    })),
    holes: gameState.holes.map(h => ({ number: h.number, par: h.par ?? null })),
    presses: (gameState.presses ?? []).map(p => ({ startingHole: p.startingHole, stackCount: p.stackCount })),
    matchFormat: gameState.matchFormat as '1v1' | '2v2',
    startingHole: gameState.startingHole,
    stakes: gameState.stakes,
    totalAmountForA: localSettlement.totalAmountForA,
    nassauAmountForA,
    pressAmountForA,
  };
  const riCards = generateRoundIntelligence(riInput, 2);

  const strokeImpactPerPlayer = (() => {
    const allPlayers = gameState.teams.flatMap(t => t.players);
    const teamAIds = new Set(gameState.teams[0]?.players.map(p => p.id) ?? []);
    const teamBIds = new Set(gameState.teams[1]?.players.map(p => p.id) ?? []);
    const allHoles = [...frontHoles, ...backHoles];
    return allPlayers.map(player => {
      const opponentIds = teamAIds.has(player.id) ? [...teamBIds] : [...teamAIds];
      let wins = 0, ties = 0, losses = 0;
      for (const holeNum of allHoles) {
        const myScore = gameState.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
        if (!myScore || !myScore.receivesStroke || myScore.netScore === null) continue;
        const oppNets = opponentIds
          .map(oid => gameState.scores.find(s => s.playerId === oid && s.holeNumber === holeNum)?.netScore)
          .filter((n): n is number => n !== null && n !== undefined);
        if (oppNets.length === 0) continue;
        const oppBest = Math.min(...oppNets);
        if (myScore.netScore < oppBest) wins++;
        else if (myScore.netScore === oppBest) ties++;
        else losses++;
      }
      return { name: player.name, wins, ties, losses, total: wins + ties + losses };
    }).filter(p => p.total > 0);
  })();

  const bsuRanked = computeBestStrokeUtilization(
    gameState.teams.flatMap(t => t.players).map(player => {
      const impact = strokeImpactPerPlayer.find(p => p.name === player.name);
      return {
        name: player.name,
        strokesReceived: player.strokesReceived,
        withStrokeWins: impact?.wins ?? 0,
        withStrokeTies: impact?.ties ?? 0,
        withStrokeLosses: impact?.losses ?? 0,
      };
    })
  );

  const team1Name = gameState.matchFormat === '1v1'
    ? (gameState.teams[0]?.players[0]?.name ?? 'Team 1')
    : `${gameState.teams[0]?.players[0]?.name ?? ''} & ${gameState.teams[0]?.players[1]?.name || ''}`;
  const team2Name = gameState.matchFormat === '1v1'
    ? (gameState.teams[1]?.players[0]?.name ?? 'Team 2')
    : `${gameState.teams[1]?.players[0]?.name ?? ''} & ${gameState.teams[1]?.players[1]?.name || ''}`;

  const strokeProtectedHoles = (() => {
    const allPlayers = gameState.teams.flatMap(t => t.players);
    const teamAIds = new Set(gameState.teams[0]?.players.map(p => p.id) ?? []);
    const teamBIds = new Set(gameState.teams[1]?.players.map(p => p.id) ?? []);
    const allHoles = [...frontHoles, ...backHoles];
    const protectedSet = new Set<number>();
    for (const player of allPlayers) {
      const opponentIds = teamAIds.has(player.id) ? [...teamBIds] : [...teamAIds];
      for (const holeNum of allHoles) {
        const myScore = gameState.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
        if (!myScore || !myScore.receivesStroke || myScore.netScore === null) continue;
        const oppNets = opponentIds
          .map(oid => gameState.scores.find(s => s.playerId === oid && s.holeNumber === holeNum)?.netScore)
          .filter((n): n is number => n !== null && n !== undefined);
        if (oppNets.length === 0) continue;
        const oppBest = Math.min(...oppNets);
        if (myScore.netScore <= oppBest) protectedSet.add(holeNum);
      }
    }
    return protectedSet.size;
  })();

  const getScoreEntry = (playerId: string, holeNumber: number) => {
    return gameState.scores.find(s => s.playerId === playerId && s.holeNumber === holeNumber) ?? null;
  };

  const getPlayerScoreDisplay = (player: GameState['teams'][0]['players'][0], holeNumber: number): string => {
    const score = getScoreEntry(player.id, holeNumber);
    if (!score || score.grossScore === null || score.grossScore === undefined) return '-';
    const hole = gameState.holes.find(h => h.number === holeNumber);
    if (!hole) return String(score.grossScore);
    const receivesStroke = playerReceivesStrokeOnHole(player, hole);
    if (receivesStroke && score.netScore !== null && score.netScore !== undefined && score.grossScore !== score.netScore) {
      return `${score.grossScore}/${score.netScore}`;
    }
    return String(score.grossScore);
  };

  const getPlayerTotalDisplay = (playerId: string, holes: number[]): string => {
    const scores = gameState.scores.filter(
      s => s.playerId === playerId && holes.includes(s.holeNumber) && s.grossScore !== null && s.grossScore !== undefined
    );
    if (scores.length === 0) return '-';
    const grossTotal = scores.reduce((sum, s) => sum + (s.grossScore ?? 0), 0);
    const netTotal = scores.reduce((sum, s) => sum + (s.netScore ?? s.grossScore ?? 0), 0);
    if (grossTotal !== netTotal) {
      return `${grossTotal}/${netTotal}`;
    }
    return String(grossTotal);
  };

  const getHoleInfo = (holeNumber: number) => {
    return gameState.holes.find(h => h.number === holeNumber);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-[#064E3B] text-white px-4 pb-4 sticky top-0 z-50 safe-area-top">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-[#FACC15]" />
            <div>
              <h1 className="font-bold text-lg">Round Complete & Analysis</h1>
              <p className="text-xs text-emerald-300">
                {gameState.gameMode === 'TwoDownAuto' ? 'Two Down Auto' : 'DoublePress!'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Gate B — Review Local Scores banner.
          Shown when the caller is in recovery_required mode and the user has
          chosen to review their locally-stored scores before deciding how to
          resolve the split-brain state.  This banner MUST be the first visible
          element so the user cannot miss it — it is not a secondary label. */}
      {reviewLocalScores && (
        <div
          className="bg-amber-50 border-b-2 border-amber-400 px-4 py-3"
          data-testid="banner-review-local-scores"
        >
          <div className="max-w-4xl mx-auto flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-amber-800">Local scores — not server-confirmed</p>
              <p className="text-xs text-amber-700 mt-0.5">
                These scores are from your device only. Settlement and money results are hidden until the round is resolved.
              </p>
            </div>
            {onReturnToRecovery && (
              <button
                onClick={onReturnToRecovery}
                className="flex-shrink-0 text-xs font-semibold text-amber-800 underline underline-offset-2 whitespace-nowrap pt-0.5"
                data-testid="button-back-to-recovery"
              >
                ← Back to Recovery
              </button>
            )}
          </div>
        </div>
      )}

      {showRazzyStrip && playerStats && (
        <div className="bg-[#064E3B] border-b border-[#053f30]" data-testid="razzy-strip">
          <div className="max-w-4xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {playerStats.lifetimeNet === 0 && playerStats.headToHead.length === 0 ? (
              <p className="text-sm text-gray-300 italic">Your season record starts now</p>
            ) : (
              <>
                <p className={`text-sm font-semibold ${playerStats.lifetimeNet >= 0 ? 'text-[#FACC15]' : 'text-red-400'}`} data-testid="text-lifetime-net">
                  You're {playerStats.lifetimeNet >= 0 ? '+' : ''}${playerStats.lifetimeNet} this season
                </p>
                {(() => {
                  const opponentNames = gameState.teams.flatMap(t => t.players.map(p => p.name.toLowerCase()));
                  const h2hMatch = playerStats.headToHead.find(h =>
                    opponentNames.some(n => h.opponentName.toLowerCase() === n || n.includes(h.opponentName.toLowerCase()))
                  );
                  if (!h2hMatch) return null;
                  return (
                    <p className="text-sm text-[#FACC15]" data-testid="text-head-to-head">
                      You're {h2hMatch.wins}–{h2hMatch.losses} vs {h2hMatch.opponentName} this year
                    </p>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}

      <main className="px-4 py-4 pb-24 max-w-4xl mx-auto space-y-4">
        {showRazzyStrip && playerStats?.strokePerformance && (playerStats.strokePerformance.strokeHoles.total > 0 || playerStats.strokePerformance.straightHoles.total > 0) && (() => {
          const sp = playerStats.strokePerformance!;
          const totalHolesPlayed = sp.strokeHoles.total + sp.straightHoles.total;
          const isSmallSample = sp.strokeHoles.total < 18;
          const delta = sp.strokeHoles.winRate - sp.straightHoles.winRate;
          return (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="stroke-performance-card">
            <div className="bg-[#064E3B] text-white px-4 py-2">
              <h2 className="font-bold text-sm uppercase tracking-wide">Handicap Performance</h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1">Stroke Holes</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="text-stroke-win-rate">
                    {sp.strokeHoles.winRate}%
                  </p>
                  <p className="text-xs text-gray-600 mt-1" data-testid="text-stroke-record">
                    {sp.strokeHoles.wins}W – {sp.strokeHoles.ties}T – {sp.strokeHoles.losses}L
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-testid="text-stroke-hole-count">
                    {sp.strokeHoles.total} holes played
                  </p>
                  {sp.isTeamFormat && sp.strokeHoles.total > 0 && (
                    <p className="text-xs font-medium text-emerald-600 mt-1" data-testid="text-stroke-counted-rate">
                      Counted score: {sp.strokeHoles.countedScoreRate}%
                    </p>
                  )}
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Straight Holes</p>
                  <p className="text-2xl font-bold text-gray-700" data-testid="text-straight-win-rate">
                    {sp.straightHoles.winRate}%
                  </p>
                  <p className="text-xs text-gray-600 mt-1" data-testid="text-straight-record">
                    {sp.straightHoles.wins}W – {sp.straightHoles.ties}T – {sp.straightHoles.losses}L
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-testid="text-straight-hole-count">
                    {sp.straightHoles.total} holes played
                  </p>
                  {sp.isTeamFormat && sp.straightHoles.total > 0 && (
                    <p className="text-xs font-medium text-gray-500 mt-1" data-testid="text-straight-counted-rate">
                      Counted score: {sp.straightHoles.countedScoreRate}%
                    </p>
                  )}
                </div>
              </div>
              {sp.strokeHoles.total > 0 && sp.straightHoles.total > 0 && (
                <p className={`text-xs text-center mt-3 italic ${isSmallSample ? 'text-gray-300' : 'text-gray-500'}`} data-testid="text-stroke-comparison">
                  {delta > 0
                    ? `Win rate is ${delta.toFixed(1)}% higher on stroke holes`
                    : delta < 0
                      ? `Win rate is ${Math.abs(delta).toFixed(1)}% lower on stroke holes`
                      : 'Same win rate on stroke and straight holes'}
                  {isSmallSample && ' · Small sample size'}
                </p>
              )}
              {isSmallSample && sp.strokeHoles.total > 0 && (
                <p className="text-xs text-gray-300 text-center mt-1" data-testid="text-sample-warning">
                  Based on {totalHolesPlayed} total holes — play more rounds for reliable trends
                </p>
              )}
            </div>
          </div>
          );
        })()}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-800 text-white px-4 py-2">
            <h2 className="font-bold text-sm uppercase tracking-wide">Final Scorecard</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100 border-b">
                  <th className="px-2 py-1 text-left font-semibold sticky left-0 bg-gray-100 z-10">Hole</th>
                  {[...Array(9)].map((_, i) => (
                    <th key={i} className="px-1 py-1 text-center font-semibold min-w-[28px]">
                      {frontHoles[i]}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-center font-semibold bg-gray-200">Out</th>
                  {[...Array(9)].map((_, i) => (
                    <th key={i + 9} className="px-1 py-1 text-center font-semibold min-w-[28px]">
                      {backHoles[i]}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-center font-semibold bg-gray-200">In</th>
                  <th className="px-2 py-1 text-center font-semibold bg-gray-300">Tot</th>
                </tr>
                <tr className="bg-blue-50 border-b text-xs">
                  <td className="px-2 py-0.5 text-left text-blue-600 sticky left-0 bg-blue-50 z-10">Yds</td>
                  {frontHoles.map(hole => (
                    <td key={hole} className="px-1 py-0.5 text-center text-blue-600">
                      {getHoleInfo(hole)?.yardage || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-100">
                    {frontHoles.reduce((sum, h) => sum + (getHoleInfo(h)?.yardage || 0), 0)}
                  </td>
                  {backHoles.map(hole => (
                    <td key={hole} className="px-1 py-0.5 text-center text-blue-600">
                      {getHoleInfo(hole)?.yardage || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-100">
                    {backHoles.reduce((sum, h) => sum + (getHoleInfo(h)?.yardage || 0), 0)}
                  </td>
                  <td className="px-2 py-0.5 text-center text-blue-600 bg-blue-200">
                    {gameState.holes.reduce((sum, h) => sum + (h.yardage || 0), 0)}
                  </td>
                </tr>
                <tr className="bg-amber-50 border-b text-xs">
                  <td className="px-2 py-0.5 text-left text-amber-700 sticky left-0 bg-amber-50 z-10">Hdcp</td>
                  {frontHoles.map(hole => (
                    <td key={hole} className="px-1 py-0.5 text-center text-amber-700">
                      {getHoleInfo(hole)?.strokeIndex || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-100"></td>
                  {backHoles.map(hole => (
                    <td key={hole} className="px-1 py-0.5 text-center text-amber-700">
                      {getHoleInfo(hole)?.strokeIndex || '-'}
                    </td>
                  ))}
                  <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-100"></td>
                  <td className="px-2 py-0.5 text-center text-amber-700 bg-amber-200"></td>
                </tr>
                <tr className="bg-gray-50 border-b">
                  <td className="px-2 py-1 text-left text-gray-500 sticky left-0 bg-gray-50 z-10">Par</td>
                  {frontHoles.map(hole => (
                    <td key={hole} className="px-1 py-1 text-center text-gray-500">
                      {getHoleInfo(hole)?.par}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center text-gray-500 bg-gray-100">
                    {frontHoles.reduce((sum, h) => sum + (getHoleInfo(h)?.par || 0), 0)}
                  </td>
                  {backHoles.map(hole => (
                    <td key={hole} className="px-1 py-1 text-center text-gray-500">
                      {getHoleInfo(hole)?.par}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center text-gray-500 bg-gray-100">
                    {backHoles.reduce((sum, h) => sum + (getHoleInfo(h)?.par || 0), 0)}
                  </td>
                  <td className="px-2 py-1 text-center text-gray-500 bg-gray-200">
                    {gameState.holes.reduce((sum, h) => sum + h.par, 0)}
                  </td>
                </tr>
              </thead>
              <tbody>
                {gameState.teams.flatMap(team =>
                  team.players.map(player => (
                    <tr key={player.id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-1 font-bold text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">
                        {player.name}
                      </td>
                      {frontHoles.map(hole => (
                        <td key={hole} className="px-1 py-1 text-center">
                          {getPlayerScoreDisplay(player, hole)}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-center font-semibold bg-gray-100">
                        {getPlayerTotalDisplay(player.id, frontHoles)}
                      </td>
                      {backHoles.map(hole => (
                        <td key={hole} className="px-1 py-1 text-center">
                          {getPlayerScoreDisplay(player, hole)}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-center font-semibold bg-gray-100">
                        {getPlayerTotalDisplay(player.id, backHoles)}
                      </td>
                      <td className="px-2 py-1 text-center font-bold bg-gray-200">
                        {getPlayerTotalDisplay(player.id, [...frontHoles, ...backHoles])}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gate B: In reviewLocalScores mode, suppress the entire Match Results
            section. The round is server-active; displaying any settlement —
            even "tied / no payouts" — would misrepresent the state. Show a
            clear notice instead. */}
        {reviewLocalScores ? (
          <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden" data-testid="review-local-settlement-suppressed">
            <div className="bg-amber-600 text-white px-4 py-2">
              <h2 className="font-bold text-sm uppercase tracking-wide">Match Results</h2>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm text-amber-800 font-medium">Settlement hidden — round not yet confirmed by server.</p>
              <p className="text-xs text-gray-500 mt-1">Resolve the recovery options to see final results.</p>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-[#064E3B] text-white px-4 py-2">
            <h2 className="font-bold text-sm uppercase tracking-wide">Match Results</h2>
          </div>
          <div className="px-4 py-3 space-y-2">
            {settlementLoading ? (
              <div className="flex items-center justify-center py-2" data-testid="text-settlement-loading">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <p className="text-sm text-gray-500">Calculating...</p>
              </div>
            ) : settlementFetchFailed ? (
              // Gate B: explicit error state for server-backed rounds.
              // Never silently substitute a local estimate for confirmed server data.
              <div className="py-3 space-y-3" data-testid="settlement-fetch-error">
                <div className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">⚠</span>
                  <p className="text-sm text-gray-700 font-medium">
                    Settlement data could not be loaded from the server.
                  </p>
                </div>
                <button
                  onClick={retrySettlementFetch}
                  className="w-full text-sm font-semibold text-emerald-700 underline underline-offset-2 py-1"
                  data-testid="button-retry-settlement"
                >
                  Tap to retry
                </button>
                {/* Show a local estimate only if there's a non-zero result,
                    clearly labeled as unconfirmed so the user can't mistake
                    it for server-authoritative data. */}
                {localSettlement.totalAmountForA !== 0 && (() => {
                  const t1 = gameState.matchFormat === '1v1'
                    ? (gameState.teams[0]?.players[0]?.name ?? 'Team 1')
                    : `${gameState.teams[0]?.players[0]?.name ?? ''} & ${gameState.teams[0]?.players[1]?.name || ''}`;
                  const t2 = gameState.matchFormat === '1v1'
                    ? (gameState.teams[1]?.players[0]?.name ?? 'Team 2')
                    : `${gameState.teams[1]?.players[0]?.name ?? ''} & ${gameState.teams[1]?.players[1]?.name || ''}`;
                  const winner = localSettlement.totalAmountForA > 0 ? t1 : t2;
                  const loser = localSettlement.totalAmountForA > 0 ? t2 : t1;
                  return (
                    <p className="text-xs text-gray-500 border border-amber-200 bg-amber-50 rounded px-3 py-2 leading-relaxed" data-testid="settlement-local-estimate">
                      <span className="font-semibold text-amber-800">Estimated — not server-confirmed:</span>{' '}
                      {winner} beat {loser} — ${Math.abs(localSettlement.totalAmountForA).toFixed(2)} per player
                    </p>
                  );
                })()}
              </div>
            ) : !settlementLines || settlementLines.length === 0 ? (
              <p className="font-semibold text-sm text-gray-600 py-2" data-testid="text-settlement-tied">Match is tied – No payouts needed!</p>
            ) : settlementLines[0].amount === 0 ? (
              <div className="text-center py-3" data-testid="text-settlement-tied">
                <p className="font-bold text-lg text-gray-800">Match Tied</p>
                <p className="text-sm text-gray-500 mt-1">No payouts needed!</p>
              </div>
            ) : (
              <MatchResultCard settlementLines={settlementLines} />
            )}
            {!readOnly && settlementLines && settlementLines.length > 0 && settlementLines.some(l => l.amount > 0) && (
              <div className="mt-3">
                <Button
                  className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white font-semibold"
                  onClick={() => setStepperOpen(true)}
                  data-testid="button-settle-round"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Settle Round
                </Button>
              </div>
            )}
            {!readOnly && isAuthenticated && isParticipant && gameState.roundId && !settlementLoading && (!settlementLines || settlementLines.length === 0 || settlementLines.every(l => l.amount === 0)) && (
              <div className="mt-3" data-testid="tie-finalize-block">
                <Button
                  className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white font-semibold"
                  onClick={handleFinalizeTiedRound}
                  disabled={tieFinalizing}
                  data-testid="button-finalize-tie"
                >
                  {tieFinalizing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Finalizing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Finalize Round (Tie, $0)
                    </>
                  )}
                </Button>
                {tieFinalizeError && (
                  <p className="text-xs text-red-600 mt-2 text-center" data-testid="tie-finalize-error">{tieFinalizeError}</p>
                )}
              </div>
            )}
            {!readOnly && userMissingPaymentHandles && !profileSaved && settlementLines && settlementLines.length > 0 && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="inline-payment-handles">
                <p className="text-xs font-medium text-gray-700 mb-2">Add payment handles so opponents can pay you faster</p>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-14 text-right">Venmo</label>
                    <input
                      type="text"
                      placeholder="@username"
                      value={venmoInput}
                      onChange={(e) => setVenmoInput(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid="input-venmo-handle"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 w-14 text-right">PayPal</label>
                    <input
                      type="text"
                      placeholder="email or username"
                      value={paypalInput}
                      onChange={(e) => setPaypalInput(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid="input-paypal-handle"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="text-xs h-7 self-end"
                    disabled={profileSaving || (!venmoInput.trim() && !paypalInput.trim())}
                    onClick={handleSavePaymentHandles}
                    data-testid="button-save-payment-handles"
                  >
                    {profileSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
            {profileSaved && (
              <p className="text-xs text-green-600 mt-2" data-testid="text-payment-handles-saved">Payment handles saved!</p>
            )}
          </div>
          {!readOnly && stepperOpen && settlementLines && settlementLines.length > 0 && (() => {
            const filteredLines = settlementLines.filter(l => l.amount > 0);
            const winnerNames = filteredLines.map(l => l.toName);
            const loserNames = filteredLines.map(l => l.fromName);
            return (
              <SettlementStepper
                lines={filteredLines}
                roundId={gameState.roundId}
                courseName={gameState.courseName}
                matchFormat={gameState.matchFormat}
                winningTeamNames={winnerNames}
                losingTeamNames={loserNames}
                onClose={() => setStepperOpen(false)}
              />
            );
          })(
          )}
        </div>
        )} {/* end of reviewLocalScores ternary — closes the non-review "Match Results" card */}

        {strokeImpactPerPlayer.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="stroke-impact-card">
            <div className="bg-[#064E3B] text-white px-4 py-2">
              <h2 className="font-bold text-sm uppercase tracking-wide">Stroke Impact</h2>
            </div>
            <div className="px-4 py-3 space-y-2">
              {strokeProtectedHoles > 0 && (
                <p className="text-xs text-gray-500 pb-1" data-testid="stroke-protected-holes">
                  Handicap strokes protected {strokeProtectedHoles} {strokeProtectedHoles === 1 ? 'hole' : 'holes'} today
                </p>
              )}
              {strokeImpactPerPlayer.map(p => (
                <div key={p.name} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0" data-testid={`stroke-impact-${p.name}`}>
                  <span className="font-semibold text-sm text-gray-900">{p.name}</span>
                  <span className="text-sm">
                    <span className="text-emerald-700 font-bold">{p.wins} won</span>
                    <span className="text-gray-400"> / </span>
                    <span className="text-gray-600 font-bold">{p.ties} tied</span>
                    <span className="text-gray-400"> / </span>
                    <span className="text-red-600 font-bold">{p.losses} lost</span>
                    <span className="text-gray-400 text-xs"> despite strokes</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <RoundIntelligenceSection cards={riCards} />

        <BestStrokeUtilizationSection ranked={bsuRanked} />

        {readOnly && !isOrganizer && !isAttached && !!gameState.roundId && (
          <div className="bg-white rounded-xl shadow-sm border border-emerald-200 overflow-hidden" data-testid="ledger-nudge-card">
            <div className="p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <BookOpen className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900 text-sm">Add this round to my Ledger</p>
                <p className="text-xs text-gray-500 mt-0.5">18-hole history · betting results · lifetime stats · head-to-head record</p>
                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      const returnTo = encodeURIComponent(`/rounds/${gameState.roundId}/live`);
                      window.location.href = `/auth?mode=signin&returnTo=${returnTo}`;
                    } else {
                      setShowAttachModal(true);
                    }
                  }}
                  className="mt-3 w-full bg-[#064E3B] text-white font-bold py-2.5 rounded-xl text-sm active:bg-emerald-800 transition-colors"
                  data-testid="button-add-to-ledger"
                >
                  {isAuthenticated ? 'Add to My Ledger' : 'Sign In to Add to My Ledger'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Gate B: In reviewLocalScores mode, show a "Back to Recovery" button at
            the bottom of the page as a second affordance (the primary is in the
            top banner). Both are shown so the user can return without scrolling. */}
        {reviewLocalScores && onReturnToRecovery && (
          <div className="flex flex-col gap-3 pt-4">
            <Button
              variant="outline"
              className="w-full gap-2 border-amber-400 text-amber-800 hover:bg-amber-50"
              onClick={onReturnToRecovery}
              data-testid="button-back-to-recovery-bottom"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Recovery Options
            </Button>
          </div>
        )}

        {!readOnly && !reviewLocalScores && (
          <div className="flex flex-col gap-3 pt-4">
            {editScoresError && (
              <p className="text-sm text-red-600 text-center px-1" data-testid="text-edit-scores-error">{editScoresError}</p>
            )}
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={onEditScores}
              data-testid="button-edit-scores"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Score Entry
            </Button>
            <Button
              className="w-full gap-2 bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15]"
              onClick={onNewRound}
              data-testid="button-new-match"
            >
              <RotateCcw className="w-4 h-4" />
              Start New Match
            </Button>
            <Button
              variant="ghost"
              className="w-full gap-2"
              onClick={onNewRound}
              data-testid="button-home"
            >
              <Home className="w-4 h-4" />
              Back to Main Menu
            </Button>
          </div>
        )}
      </main>
      {showAttachModal && gameState.roundId && (
        <AttachToRoundModal
          roundId={gameState.roundId}
          groupId={groupId ?? null}
          isFinalized={!!gameState.isFinalized}
          onClose={() => setShowAttachModal(false)}
        />
      )}
    </div>
  );
}
